import { PathName } from './customerSupportWidget/types';

/**
 * Returns the path portion of the URL hash (e.g. /embedded/annotator/patientId).
 * Strips query params so consumers get a clean path for segment parsing.
 * Use this when useLocation/useParams aren't available (e.g. Header, VideaNotesButton).
 */
export const getPathFromHash = (): string => {
  if (typeof window === 'undefined') return '/';
  const hash = window.location.hash.slice(1) || '/';
  return hash.split('?')[0];
};

/**
 * Annotator path segment indices.
 * /embedded/annotator/patientId[/imageId] -> patientId at 3, imageId at 4
 * /annotator/patientId[/imageId] -> patientId at 2, imageId at 3
 */
const ANNOTATOR_SEGMENTS = {
  embedded: { patientId: 3, imageId: 4 },
  standard: { patientId: 2, imageId: 3 },
} as const;

/**
 * Extracts patient ID from the current annotator URL.
 * Reads from hash - use when useParams doesn't work (nested router structure).
 */
export const getAnnotatorPatientIdFromHash = (): string | null => {
  const path = getPathFromHash();
  const segments = path.split('/');
  const isEmbedded = segments[1] === 'embedded';
  const indices = isEmbedded
    ? ANNOTATOR_SEGMENTS.embedded
    : ANNOTATOR_SEGMENTS.standard;
  const patientId = segments[indices.patientId] ?? null;
  const isAnnotatorPath =
    (isEmbedded && segments[2] === 'annotator') ||
    (!isEmbedded && segments[1] === 'annotator');
  return isAnnotatorPath ? patientId : null;
};

/**
 * Returns true if the current annotator URL has an imageId segment.
 */
export const hasImageIdInAnnotatorHash = (): boolean => {
  const path = getPathFromHash();
  const segments = path.split('/');
  const isEmbedded = segments[1] === 'embedded';
  const indices = isEmbedded
    ? ANNOTATOR_SEGMENTS.embedded
    : ANNOTATOR_SEGMENTS.standard;
  return segments.length > indices.imageId && !!segments[indices.imageId];
};

export const parsePathname = (pathname: string, isEmbedded = false) => {
  const splitUrl = pathname.split('/');
  const currentPathName = isEmbedded ? splitUrl[2] : splitUrl[1];
  const patientId = (isEmbedded ? splitUrl[3] : splitUrl[2]) ?? null;
  const imageId = splitUrl.length > 3 ? splitUrl[splitUrl.length - 1] : null;
  let currentPage: PathName;
  if (currentPathName === 'annotator' && imageId) {
    currentPage = PathName.single_image_view;
  } else if (currentPathName === 'annotator' && patientId) {
    currentPage = PathName.fmx;
  } else {
    currentPage = PathName.other;
  }
  return { currentPage, patientId, imageId };
};
